import { Box, Stack, Typography } from "@mui/joy";
import { PaymentButton } from "components/task/payment/elements";
import React, { useEffect, useRef, useState } from "react";
import pcAndMobileImg from "static/image/payment/pc_and_mobile.png";
import reviewAvatar1Img from "static/image/payment/review_avatar_1.svg";
import reviewAvatar2Img from "static/image/payment/review_avatar_2.svg";
import reviewAvatar3Img from "static/image/payment/review_avatar_3.svg";
import manyImg from "static/image/payment/many.svg";
import cheaperImg from "static/image/payment/cheaper.svg";
import sponsorImg from "static/image/payment/sponsor_payment.png";
import fivestarsImg from "static/image/onboarding/fivestars.svg";
import useSafeAreaInsets from "hooks/useSafeAreaInsets";
import useGetUrls from "hooks/useGetUrls";
import { sendMessage } from "utils/webview";
import useIsInappWebview from "hooks/useIsInappWebview";
import { theme } from "styles/theme";
import FloatingArea from "components/common/FloatingArea";
import { extractHexColor } from "../../../styles/hexOpacity";
import { useRecoilState } from "recoil";
import { paymentOrderAtom } from "../../../recoil/paymentOrderAtom";
import { useQuery } from "@tanstack/react-query";
import {
  PAYMENT_DOMAIN,
  PAYMENT_ORDER_DETAIL_ENDPOINT,
  paymentApi,
} from "api/paymentApi";
import { userAtom } from "../../../recoil/userAtom";
import CustomTypography from "../../common/CustomTypography";
import CustomTypographyHighlight from "../../common/CustomTypographyHighlight";
import ContentTemplateV2 from "../content/ContentTemplateV2";
import UserInfo from "../../user/UserInfo";
import CxButton from "../../sidebar/CXButton";
import LinkV2 from "../../common/LinkV2";
import ReviewItem from "./elements/ReviewItem";
import rocketImg from "../../../static/image/payment/rocket.svg";
import CompareWithCounseling365 from "./elements/CompareWithCounseling365";
import useMixpanelWeb from "../../../hooks/useMixpanelWeb";

interface ComparisonItem {
  type: "quantity" | "cost";
  title: string;
  text: string;
  icon: string;
}

const comparisonItems: ComparisonItem[] = [
  {
    type: "quantity",
    title: "심리상담보다\n3배 더 많이",
    text: "디스턴싱에서는 매일 30분 정도의 활동이 제공됩니다. 일주일에 약 150분에 달하는 시간이죠. 반면 일반적인 심리상담은 일주일에 50분만 진행합니다.",
    icon: manyImg,
  },
  {
    type: "cost",
    title: "심리상담보다\n4배 더 싸게",
    text: "디스턴싱은 100일에 365,000원에 이용할 수 있습니다. 일주일에 2만 원 대에 불과합니다. 반면 일반적인 심리상담은 일주일에 10만 원이 듭니다.",
    icon: cheaperImg,
  },
];

export default function PaymentContentCarePass() {
  const floatingAreaRef = useRef<HTMLDivElement>(null);
  const isInappWebview = useIsInappWebview();

  const paddingX = 4.5;

  const [user, setUser] = useRecoilState(userAtom);

  const [paymentOrderState, setPaymentOrderState] =
    useRecoilState(paymentOrderAtom);

  const isFirstOrder = paymentOrderState?.isFirst;
  const fullWidth = isFirstOrder ? true : !!isInappWebview;

  const { refetch: getPaymentOrder } = useQuery(
    [PAYMENT_DOMAIN, PAYMENT_ORDER_DETAIL_ENDPOINT],
    () => paymentApi.getOrderDetail(),
    {
      enabled: false,
      onSuccess: (data) => {
        if (data && data.result) {
          setPaymentOrderState(data.result);
        }
      },
    }
  );

  const setMixPanel = useMixpanelWeb({
    eventName: "결제 페이지 진입",
    params: {
      event_source: "app",
    },
  });

  // 결제 성공 후 재진입 시 page view 관련 이벤트(meta, firebase, appsflyer, mixpanel) 전송 차단 플래그
  const suppressPageViewRef = useRef<boolean>(
    (() => {
      try {
        return localStorage.getItem("suppress_payment_page_view") === "1";
      } catch (e) {
        return false;
      }
    })()
  );

  useEffect(() => {
    // 플래그는 1회성으로 사용 후 제거
    if (suppressPageViewRef.current) {
      try {
        localStorage.removeItem("suppress_payment_page_view");
      } catch (e) {}
    }
  }, []);

  function useEffectOnce(callback: () => void) {
    const ref = useRef(false);

    useEffect(() => {
      if (ref.current) return;
      ref.current = true;

      if (typeof callback === "function") {
        callback();
      }
    }, []);
  }

  useEffectOnce(() => {
    if (!suppressPageViewRef.current) {
      setMixPanel();
    }
  });

  useEffect(() => {
    getPaymentOrder();
  }, []);

  const { companyInfoUrl, privacyPolicyUrl, servicePolicyUrl } = useGetUrls();
  const hasSentSafeAreaColorRef = useRef(false);

  useEffect(() => {
    if (hasSentSafeAreaColorRef.current) return;
    if (!(isInappWebview && paymentOrderState)) return;

    const topBackground = extractHexColor(theme.vars.palette.Background.Level3);
    const bottomBackground = extractHexColor(
      theme.vars.palette.Background.Level1
    );

    if (topBackground && bottomBackground) {
      sendMessage(
        JSON.stringify({
          event: "set_safearea_color",
          params: {
            topColor: `${topBackground}`,
            bottomColor: `${bottomBackground}`,
          },
        })
      );
      hasSentSafeAreaColorRef.current = true;
    }
  }, [isInappWebview, paymentOrderState]);

  const { bottom: safeBottom } = useSafeAreaInsets();
  const [containerHeight, setContainerHeight] = useState<number>(
    window.innerHeight - safeBottom
  );
  const [floatingAreaHeight, setFloatingAreaHeight] = useState<number>(0);

  useEffect(() => {
    setTimeout(() => {
      setFloatingAreaHeight(floatingAreaRef.current?.offsetHeight || 0);
    }, 100);
  }, [floatingAreaRef.current]);

  return (
    <ContentTemplateV2
      fullBleed
      disableBottomSafeArea
      isWide
      setContainerHeight={setContainerHeight}
    >
      {containerHeight > 0 && (
        <Stack
          sx={{
            pb: "150px",
            backgroundColor: theme.vars.palette.Background.Level3,
          }}
        >
          {/*영역 1: ~님의 프로그램 */}
          <Stack
            spacing={5}
            justifyContent="center"
            alignItems={"center"}
            sx={{
              width: "100%",
              maxWidth: "100%",
              mx: "auto",
              px: paddingX,
              pt: 6,
              pb: 8,
            }}
          >
            <Stack
              spacing={3}
              sx={{
                width: "100%",
                maxWidth: "660px",
                textAlign: "center",
              }}
            >
              <CustomTypography
                variant={"H1"}
                sx={{
                  paddingTop: "54px",
                  color: theme.vars.palette.Content.Strong,
                  fontWeight: 800,
                }}
              >
                {`${user?.nickname}님의 프로그램`}
              </CustomTypography>
              <CustomTypography
                variant={"Subtitle"}
                sx={{
                  color: theme.vars.palette.Content.Medium,
                  fontWeight: 500,
                }}
              >
                {"마음을 다루는 근거있고\n효과적인 방법을 제안합니다."}
              </CustomTypography>
            </Stack>
          </Stack>

          {/*영역2 3개월 이용권*/}
          <Stack
            justifyContent="center"
            alignItems={"center"}
            sx={{
              // 전체 박스 스타일
              backgroundColor: theme.vars.palette.Surface.Highlight,
              width: "100%",
              maxWidth: "100%",
              mx: "auto",
              px: paddingX,
              // height: `calc(${containerHeight}px - ${floatingAreaHeight}px)`,
              // pt: "50px",
            }}
          >
            <Box
              sx={{
                maxWidth: "660px",
                paddingY: "60px", // 내부 여백
                width: "100%", // 임시로 폭 지정
                margin: "0 auto", // 가운데 정렬 (선택 사항)
              }}
            >
              <Stack spacing={3}>
                {/* 첫 줄(좌우 배치) */}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    px: 0.6,
                  }}
                >
                  <CustomTypography
                    variant={"H3"}
                    sx={{
                      p: 0,
                      color: theme.vars.palette.Content.Negative,
                      fontWeight: 700,
                    }}
                  >
                    100일 완성
                  </CustomTypography>
                  <CustomTypography
                    variant={"H3"}
                    sx={{
                      p: 0,
                      color: theme.vars.palette.Content.Negative,
                      fontWeight: 700,
                    }}
                  >
                    {"36,500원"}
                  </CustomTypography>
                </Stack>

                <CustomTypography
                  variant={"Subtitle"}
                  sx={{
                    p: 0,
                    color: theme.vars.palette.Content.SoftHighlight,
                    fontWeight: 500,
                  }}
                >
                  {
                    "100일 동안 매주 참여한 후에도 변화가 느껴지지 않으신다면 비용 전액을 돌려드립니다."
                  }
                </CustomTypography>
              </Stack>
            </Box>
          </Stack>

          {/*변화를 위한 투자*/}
          <Stack
            py={10}
            spacing={5}
            justifyContent="center"
            alignItems={"center"}
            sx={{
              backgroundColor: theme.vars.palette.Background.Level3,
              width: "100%",
              mx: "auto",
              px: paddingX,
            }}
          >
            <Stack
              direction="column"
              alignItems="start"
              spacing={"20px"}
              sx={{ width: "100%", maxWidth: "660px" }}
            >
              <img
                src={rocketImg}
                alt={"empty"}
                style={{
                  width: "100%",
                  maxWidth: "35px",
                }}
              />
              <CustomTypography
                variant={"H2"}
                sx={{
                  p: 0,
                  color: theme.vars.palette.Content.Strong,
                  fontWeight: 700,
                }}
              >
                {"변화를 위한 투자,\n하루 3천 원"}
              </CustomTypography>
            </Stack>

            <Stack
              spacing={3}
              direction="column"
              alignItems="start"
              sx={{ width: "100%", maxWidth: "660px" }}
            >
              <CustomTypographyHighlight
                variant={"Subtitle"}
                sx={{
                  color: theme.vars.palette.Content.Medium,
                  fontWeight: 500,
                  p: 0,
                }}
                highlightedText={"커피 한 잔 가격"}
                highlightVariant={"Subtitle"}
                highlightSx={{
                  color: theme.vars.palette.Content.Strong,
                  fontWeight: 700,
                }}
              >
                {"프로그램 이용에 드는 비용은 하루에 3천 원 대에 불과합니다. 커피 한 잔 가격으로 당신의 삶을 완전히 바꿀 기회입니다.\n\n" +
                  "혁신적인 방법으로 완성한 전 세계 유일의 통합 인지치료, 디스턴싱. 지금 바로 시작하세요."}
              </CustomTypographyHighlight>
            </Stack>
          </Stack>

          {/*영역4: 리뷰*/}
          <Stack
            py={10}
            spacing={"30px"}
            justifyContent="center"
            alignItems={"center"}
            sx={{
              backgroundColor: theme.vars.palette.Content.Strong,
              width: "100%",
              mx: "auto",
              px: paddingX,
              // height: `calc(${containerHeight}px - ${floatingAreaHeight}px)`,
              // pt: "50px",
            }}
          >
            <Box
              sx={{
                maxWidth: "660px",
              }}
            >
              <Stack alignItems="center" spacing={6}>
                <Stack alignItems="center" spacing={2}>
                  <img
                    src={fivestarsImg}
                    alt={"empty"}
                    style={{
                      width: "100%",
                      maxWidth: "124px",
                      // objectFit: "cover",
                      // objectPosition: "50% 0%",
                    }}
                  />

                  <CustomTypography
                    variant={"H2"}
                    sx={{
                      color: theme.vars.palette.Content.SoftHighlight,
                      fontWeight: 700,
                    }}
                  >
                    500+개의 5점 리뷰
                  </CustomTypography>
                </Stack>

                {/* 후기 항목들 */}
                <Stack spacing={6} alignItems="center">
                  <ReviewItem
                    avatarIcon={reviewAvatar1Img}
                    text={"생각을 바라보는\n완전히 새로운 접근"}
                  />
                  <ReviewItem
                    avatarIcon={reviewAvatar2Img}
                    text={"신선하고 사려깊은\n과학적인 프로그램"}
                  />
                  <ReviewItem
                    avatarIcon={reviewAvatar3Img}
                    text={"지금껏 받아봤던\n인지행동치료 중 최고"}
                  />
                </Stack>
              </Stack>
            </Box>
          </Stack>

          <Stack
            py={10}
            spacing={2}
            justifyContent="center"
            alignItems="center"
            sx={{
              backgroundColor: theme.vars.palette.Background.Level3,
              width: "100%",
              maxWidth: "660px",
              mx: "auto",
              position: "relative",
              overflow: "visible",
              // 2) 이미지 높이만큼 아래 공간 확보 (예: 200px 값은 이미지 크기에 맞춰 조정)
              pb: "50px",

              // 아래 섹션과 겹치지 않도록 marginBottom 등 지정
            }}
          >
            <Stack spacing={"44px"} alignItems={"center"}>
              <CustomTypography
                variant={"Body"}
                sx={{
                  color: theme.vars.palette.Content.Medium,
                  fontWeight: 510,
                }}
              >
                {"Supported by"}
              </CustomTypography>
              <img
                src={sponsorImg}
                alt={"empty"}
                style={{
                  width: "100%",
                  maxWidth: "300px",
                  paddingBottom: "60px",
                  // objectFit: "cover",
                  // objectPosition: "50% 0%",
                }}
              />
            </Stack>
            <CustomTypography
              pt={5}
              variant="Body"
              sx={{
                color: theme.vars.palette.Content.Medium,
                fontWeight: 500,
              }}
            >
              PC, 모바일 완벽 지원
            </CustomTypography>

            {/* 3) 절대 배치 박스 (이미지를 오른쪽 밖으로) */}
            <Box
              sx={{
                // 오른쪽 끝을 삐져나가게
                mr: "-60px",
                mt: 2, // 텍스트와 간격
                width: "90%",
                alignSelf: "flex-end", // 스택의 오른쪽에 배치
              }}
            >
              <img
                src={pcAndMobileImg}
                alt="pc_and_mobile"
                style={{
                  width: "100%", // 상황에 맞게 조절
                  height: "auto",
                }}
              />
            </Box>
          </Stack>

          <Stack
            py={10}
            spacing={"30px"}
            justifyContent="center"
            alignItems={"center"}
            sx={{
              backgroundColor: theme.vars.palette.Background.Level1,
              width: "100%",
              mx: "auto",
              px: paddingX,
            }}
          >
            <Stack spacing={10} sx={{ width: "100%", maxWidth: "660px" }}>
              {comparisonItems.map(({ type, title, text, icon }) => (
                <Stack
                  spacing="40px"
                  sx={{
                    borderRadius: "12px",
                    // p: "24px",
                    // backgroundColor: "white",
                    // borderWidth: "1px",
                    // borderColor: "divider",
                  }}
                >
                  <Stack
                    spacing={"20px"}
                    direction="column"
                    alignItems="start"
                    sx={{ width: "100%" }}
                  >
                    <img
                      src={icon}
                      alt={"empty"}
                      style={{
                        width: "100%",
                        maxWidth: "35px",
                      }}
                    />
                    <Typography
                      variant={"H2"}
                      sx={{
                        p: 0,
                        color: theme.vars.palette.Content.Strong,
                        fontWeight: 700,
                      }}
                    >
                      {title}
                    </Typography>
                  </Stack>
                  <Stack spacing="40px">
                    <CustomTypography
                      variant={"Subtitle"}
                      sx={{
                        p: 0,
                        color: theme.vars.palette.Content.Medium,
                        fontWeight: 500,
                      }}
                    >
                      {text}
                    </CustomTypography>
                    <CompareWithCounseling365 type={type} />
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </Stack>

          <Stack
            spacing={2}
            justifyContent="center"
            alignItems={"center"}
            sx={{
              backgroundColor: theme.vars.palette.Background.Level3,
              width: "100%",
              maxWidth: "660px",
              mx: "auto",
              px: paddingX,
              pt: isFirstOrder ? 0 : 9,
            }}
          >
            <Stack direction={"row"} spacing={3}>
              {user && <UserInfo user={user} />}
              {<CxButton type="link" />}
            </Stack>
            <Stack direction={"row"} spacing={3}>
              <LinkV2 text={"사업자 정보"} url={companyInfoUrl} />
              <LinkV2 text={"개인정보 처리방침"} url={privacyPolicyUrl} />
              <LinkV2 text={"서비스 이용약관"} url={servicePolicyUrl} />
            </Stack>
          </Stack>

          <Box>
            <FloatingArea fullWidth={fullWidth} ref={floatingAreaRef}>
              <Stack
                spacing={1.5}
                sx={{ width: "100%" }}
                alignItems="center"
                justifyContent={"space-between"}
              >
                <CustomTypography
                  variant={"Caption"}
                  sx={{
                    p: 0,
                    fontWeight: 500,
                    color: theme.vars.palette.Content.Medium,
                  }}
                >
                  {"100일 36,500원\u00A0\u00A0|\u00A0\u00A0언제든 환불 가능"}
                </CustomTypography>
                {paymentOrderState && <PaymentButton />}
              </Stack>
            </FloatingArea>
          </Box>
        </Stack>
      )}
    </ContentTemplateV2>
  );
}
